import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useShortlist } from "@/context/ShortlistContext";

const inp = "w-full h-11 bg-[#0D0D11] border border-[#D4AF37]/20 px-3 text-sm text-[#F5F3EF] focus:outline-none focus:border-[#D4AF37]";
const lbl = "block font-mono-j text-[11px] uppercase tracking-[0.18em] text-[#A09E97] mb-1.5";

const COPY = {
  quote: {
    title: "Request a quote", phone: "Phone", notes: "Tell us about your project", submit: "Send request",
    thanks: "Your request is with our team. We'll be in touch shortly.",
  },
  samples: {
    title: "Request samples", phone: "Mobile", notes: "Anything we should know? (project, timing, colours you're torn between)", submit: "Request samples",
    intro: "Our sales team will call to book your quote & design session and arrange the samples below.",
    thanks: "Our sales team will be in touch to book your quote & design session and get your samples on their way.",
  },
};

/** Quote (mode="quote") or sample (mode="samples") request dialog. Samples also collect mobile + delivery address. */
export const QuoteDialog = ({ open, onClose, items, pairings = [], mode = "quote" }) => {
  const { client } = useShortlist();
  const samples = mode === "samples";
  const c = COPY[mode];
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "", message: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setState("idle");
      setError("");
      setF((p) => ({ ...p, name: p.name || client }));
    }
  }, [open, client]);

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    setError("");
    try {
      await api.post("/quotes", { ...f, kind: mode, item_ids: items.map((i) => i.id), pairings });
      setState("sent");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setState("idle");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent className="max-w-lg max-h-[90vh] overflow-y-auto bg-[#13141C] border-[#D4AF37]/25 text-[#F5F3EF] rounded-none" data-testid="quote-dialog" data-mode={mode}>
        {state === "sent" ? (
          <div className="py-8 text-center" data-testid="quote-success">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#D4AF37]" />
            <h2 className="mt-6 font-display text-4xl">Thank you.</h2>
            <p className="mt-3 text-[#A09E97]">{c.thanks}</p>
            <button onClick={onClose} data-testid="quote-close-btn" className="mt-8 h-11 px-8 rounded-full bg-[#D4AF37] text-[#0D0D11] text-sm font-medium">Close</button>
          </div>
        ) : (
          <>
            <DialogHeader><DialogTitle className="font-display text-3xl font-normal">{c.title}</DialogTitle></DialogHeader>
            {c.intro && <p className="text-sm text-[#A09E97] -mt-2" data-testid="quote-intro">{c.intro}</p>}
            <div className="border border-[#D4AF37]/15 p-3 max-h-32 overflow-y-auto" data-testid="quote-items">
              <p className={lbl}>{pairings.length ? `${pairings.length} pairing${pairings.length === 1 ? "" : "s"} · ` : ""}{items.length} {samples ? "sample" : "product"}{items.length === 1 ? "" : "s"}</p>
              {items.map((i) => <p key={i.id} className="text-sm text-[#F5F3EF] truncate">{i.name} <span className="font-mono-j text-[11px] text-[#C5A059]">{i.code}</span></p>)}
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div><label className={lbl}>Name</label><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="quote-name" /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className={lbl}>Email</label><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inp} data-testid="quote-email" /></div>
                <div><label className={lbl}>{c.phone}</label><input required={samples} type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inp} data-testid="quote-phone" /></div>
              </div>
              {samples && (
                <div><label className={lbl}>Delivery address</label>
                  <textarea required rows={2} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Street, suburb, state, postcode" className={`${inp} h-auto py-2`} data-testid="quote-address" />
                </div>
              )}
              <div><label className={lbl}>{c.notes}</label>
                <textarea rows={samples ? 3 : 4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={`${inp} h-auto py-2`} data-testid="quote-message" />
              </div>
              {error && <p className="text-sm text-red-400" data-testid="quote-error">{error}</p>}
              <button disabled={state === "sending"} data-testid="quote-submit"
                className="w-full h-12 rounded-full bg-[#D4AF37] text-[#0D0D11] font-medium hover:bg-[#E5C158] disabled:opacity-60 transition-colors">
                {state === "sending" ? "Sending…" : c.submit}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
